import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { useAuth } from "src/context/AuthContext";
import { useAuthModal } from "src/context/AuthModalContext";
import { useCurrentPerson } from "src/utils/useCurrentPerson";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import { usePageMeta } from "src/hooks/usePageMeta";
import { captureError } from "src/utils/errorReporting";
import { trackConversion } from "src/analytics/gtag";

import QuoteWizard from "src/components/quote/QuoteWizard";
import KiitosScreen from "src/components/quote/KiitosScreen";
import TilaaBackdrop from "src/components/quote/TilaaBackdrop";
import PersonEditDialog from "src/components/PersonEditDialog";
import AsiakasEditDialog from "src/components/AsiakasEditDialog";

import { getRunId } from "src/utils/quote/quoteSession";
import {
    readDraft, writeDraft, clearDraft,
    readActiveDraftId, writeActiveDraftId, clearActiveDraftId,
} from "src/utils/quote/quoteStorage";
import {
    createDraft, patchDraft, sendDraft,
    getDraft, getActiveDraft, deleteDraft, postAnonymousEvent,
    checkAddress,
    listMeAsiakases,
    createMeAsiakas,
    createMePrivateAsiakas,
    listPumppuRequestAttachments,
} from "src/utils/quote/quoteApi";
import { defaultAsiakasFromList } from "src/utils/quote/asiakasDefault";
import { LASKUTUS_PRIVATE_SENTINEL } from "src/components/quote/LaskutusSection";
import { validatorForStep, validateAll, validateAddress, validatePump, validateReview } from "src/utils/quote/quoteValidation";
import { displayAddress } from "src/utils/quote/quoteAddress";
import { mergeLocalIntoServerRow } from "src/utils/quote/draftMerge";
import { INITIAL_DRAFT } from "src/utils/quote/quoteConstants";
import {
    REQUEST_CONSENT_TYPE,
    gate1ContentFallback,
    GATE1_FALLBACK_VERSION,
} from "src/utils/legal/acceptanceCopy";
import { useAcceptanceCopy } from "src/hooks/useAcceptanceCopy";
import { uploadPumppuRequestAttachment } from "src/utils/quote/quoteAttachments";
import { planToSvgString, svgToPngBlob } from "src/utils/laskuri/svgSnapshot";
import { floorPlanSummary } from "src/utils/laskuri/planModel";

const TOTAL_STEPS = 5;
// Lisätiedot step (index 3) — hosts the attachments UI and is the pre-claim target.
const DETAILS_STEP_INDEX = 3;
// Pumppu step (index 1) — hosts the live boom-capability re-check. Position-
// sensitive together with STEP_FIELDS[1] (puomi/linjanPituus): if step order
// ever changes, update both and QuoteWizard's STEP_COMPONENTS-derived PUMP_STEP.
const PUMP_STEP_INDEX = 1;

// Fields per step — used to determine which keys are sent in PATCH on each transition.
const STEP_FIELDS = [
    ["osoite", "lat", "lng", "placeId", "formattedAddress"],
    ["puomi", "linjanPituus"],
    ["maaraM3", "kayttokohde", "kesto", "rasitusluokka", "lujuusId", "raeKokoId", "notkeusId", "kayttoIkaId", "betonikuvaus", "betoniLaatuSelvitys"],
    ["ajoOhjeet", "kuvaus"],
    [], // step 5 — no patches, just send
];

// Flattened wizard field list — the keys a local draft may contribute when it is
// merged onto a server draft (fb#170). The backend PATCH re-whitelists the wire
// fields, so passing the full list (incl. betoniLaatuSelvitys, which is dropped
// server-side) is safe.
const WIZARD_FIELDS = STEP_FIELDS.flat();

function pick(obj, keys) {
    const out = {};
    for (const k of keys) if (k in obj) out[k] = obj[k];
    return out;
}

function inferActiveStep(draft) {
    if (Object.keys(validateAddress(draft)).length > 0) return 0;
    if (Object.keys(validatePump(draft)).length > 0) return 1;
    if (!draft.maaraM3 || !draft.kayttokohde) return 2;
    return 4; // resume on review unless something prior is missing — covers the common case
}

// "private" sentinel translation: prefer an existing typeId=2 asiakas in the
// list; fall back to null so the backend's resolveAsiakasId autocreates one.
// Spec: docs/superpowers/specs/2026-05-13-betonijerry-laskutus-step-design.md
function resolveAsiakasIdForSend(selected, asiakases) {
    if (selected === LASKUTUS_PRIVATE_SENTINEL) {
        const existing = asiakases.find((a) => a.asiakasTypeId === 2);
        return existing ? existing.asiakasId : null;
    }
    return selected ? Number(selected) : null;
}

export default function RequestForm() {
    const t = useT();
    usePageMeta({
        title: t("landing.requestForm.pageTitle"),
        description: t("landing.requestForm.pageDescription"),
        path: "/tilaa",
    });
    const { isAuthenticated, token, logout } = useAuth();
    const person = useCurrentPerson();
    const { openAuthModal } = useAuthModal();
    const { lang } = useLang();
    const navigate = useNavigate();

    const [draft, setDraft] = useState(INITIAL_DRAFT);
    const [activeStep, setActiveStep] = useState(0);
    const [maxReachedStep, setMaxReachedStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("loading"); // loading | active | submitting | done
    const [submitError, setSubmitError] = useState(null);
    const [result, setResult] = useState(null);
    const [serverDraftId, setServerDraftId] = useState(null);
    // Inline "you're continuing a previous request" notice shown above the wizard
    // when a draft was auto-resumed (replaces the old full-screen resume-prompt
    // takeover). { address, at } — null hides it. The draft is already hydrated
    // and the user is on the inferred step; this only informs + offers "Aloita
    // alusta". Dismissing it (X) keeps the draft; only "Aloita alusta" discards.
    const [resumeNotice, setResumeNotice] = useState(null);
    const [fetchedAsiakases, setFetchedAsiakases] = useState([]);
    const asiakases = useMemo(
        () => (isAuthenticated ? fetchedAsiakases : []),
        [isAuthenticated, fetchedAsiakases],
    );
    const [selectedAsiakasId, setSelectedAsiakasId] = useState("");
    // True while LaskutusSection has a visible dropdown / create-new panel.
    // Final-step submit is gated on this so the user must commit before sending.
    const [laskutusEditing, setLaskutusEditing] = useState(false);
    const [addressCheckStatus, setAddressCheckStatus] = useState("idle"); // idle | checking | error
    const [addressCheckError, setAddressCheckError] = useState(null);
    const [attachments, setAttachments] = useState([]);
    // True while AttachmentsSection has uploads in flight — gates Seuraava so the
    // Fade remount on step change can't abandon in-progress uploads (and silently
    // drop the file). Reported up via onAttachmentsUploadingChange.
    const [attachmentsUploading, setAttachmentsUploading] = useState(false);

    // Gate-1 consent copy + version, fetched from the DB (BETONIJERRY_REQUEST_CONSENT)
    // with a bundled fallback. consentVersion is stamped on the pumppuRequest at send.
    // Memoized: useAcceptanceCopy's `fallback` param is a useEffect dependency
    // compared by reference, so a fresh object every render would refetch forever.
    const gate1Fallback = useMemo(() => gate1ContentFallback(t), [t]);
    const { copy: consentCopy, version: consentVersion } = useAcceptanceCopy(
        REQUEST_CONSENT_TYPE, gate1Fallback, GATE1_FALLBACK_VERSION
    );

    // Step-2 live boom re-check: providerCount narrowed by the entered puomi.
    // forPuomi tags which value the count was computed for — StepPump only
    // renders the row when it matches the current input (no stale counts).
    const [boomCheck, setBoomCheck] = useState({ status: "idle", providerCount: null, forPuomi: null });
    const boomCheckSeqRef = useRef(0);

    // Edit-dialog state. `editDialog.kind` decides which dialog renders;
    // `asiakas` is the prefill row for the asiakas-edit case (typeId=1).
    // PersonEditDialog covers both Yhteystiedot edit and the typeId=2
    // "private asiakas" Laskutus edit (the private row IS the person's
    // billing identity, so it routes to the same form).
    const [editDialog, setEditDialog] = useState({ kind: null, asiakas: null });
    const privateAsiakas = useMemo(
        () => fetchedAsiakases.find((a) => a.asiakasTypeId === 2) || null,
        [fetchedAsiakases],
    );

    // Per-RUN id, not the persistent per-browser one (fb#511): the admin funnel
    // counts DISTINCT sessionId, so the browser id made one person's five wizard
    // runs read as one. Presence keeps the browser id — see quoteSession.js.
    // Both of this ref's consumers (postAnonymousEvent, createDraft) must carry
    // the SAME value or the backend's claim back-fill stops matching.
    const sessionIdRef = useRef(getRunId());
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);
    const personRef = useRef(person);
    useEffect(() => { personRef.current = person; }, [person]);
    const snapshotUploadedForRef = useRef(null); // pumppuRequestId - guards duplicate snapshot on send retry

    // Latest-pick AbortController for the Laskutus picker. A rapid sequence of
    // picks (private → company → private) would otherwise let in-flight PATCHes
    // resolve out of order on the server. We abort the previous request on each
    // new pick — the in-flight fetch rejects with AbortError, which we swallow.
    const selectAsiakasAbortRef = useRef(null);

    // Builds a .catch handler that auto-logs-out on 401 (silent — expected drift
    // for an expired token) and Sentry-captures anything else with the given tags.
    const handleAuthError = useCallback((entity, operation) => (err) => {
        if (err?.status === 401) {
            logout();
            return null;
        }
        captureError(err, { tags: { entity, operation } });
        return null;
    }, [logout]);

    // ------- Hydrate from a server draft row -------
    const hydrateFromServer = useCallback((row) => {
        // betoniLaatuSelvitys (the persisted trail) carries the authoritative
        // specMode — prefer it so a guided resume stays "guided" and its trail
        // survives. Fall back to inferring from spec fields only for legacy rows
        // that predate the column (selvitys is null). Manual is the safer default
        // — the user can still re-open the avustin via "Käytä avustinta sittenkin".
        const selvitys = row.betoniLaatuSelvitys ?? null;
        const hasSpec = !!(row.rasitusluokka || row.lujuusId || row.raeKokoId || row.notkeusId);
        const specMode = selvitys?.specMode ?? (hasSpec ? "manual" : null);
        const hydrated = {
            ...INITIAL_DRAFT,
            osoite: row.osoite || "",
            maaraM3: row.maaraM3 != null ? String(row.maaraM3) : "",
            kayttokohde: row.kayttokohde || "",
            lujuusId: row.lujuusId ?? "",
            raeKokoId: row.raeKokoId ?? "",
            notkeusId: row.notkeusId ?? "",
            kayttoIkaId: row.kayttoIkaId ?? 1,
            rasitusluokka: row.rasitusluokka || "",
            betonikuvaus: row.betonikuvaus || "",
            specMode,
            betoniLaatuSelvitys: selvitys,
            puomi: row.puomi != null ? String(row.puomi) : "",
            linjanPituus: row.linjanPituus != null ? String(row.linjanPituus) : "",
            kesto: row.kesto != null ? String(row.kesto) : "",
            ajoOhjeet: row.ajoOhjeet || "",
            kuvaus: row.kuvaus || "",
            lat: row.lat ?? null,
            lng: row.lng ?? null,
            placeId: row.placeId ?? null,
            formattedAddress: row.formattedAddress ?? null,
            // Synthesize feasibility — server has lat/lng but not full check; treat as deliverable=true if lat/lng present.
            feasibility: row.lat != null
                ? { geocoded: true, deliverable: true, lat: row.lat, lng: row.lng }
                : null,
        };
        setDraft(hydrated);
        setServerDraftId(row.pumppuRequestId);
        // Hydrate the Laskutus picker from whatever asiakasId is currently stamped
        // on the draft row. Without this, reload reverts the picker to the FE
        // default (defaultAsiakasFromList) and silently overwrites the user's pick
        // at the next /send. Direct setState (not functional) intentionally beats
        // the asiakases-fetch auto-pick if both fire in the same tick.
        if (row.asiakasId != null) {
            setSelectedAsiakasId(String(row.asiakasId));
        }
        const inferred = inferActiveStep(hydrated);
        setActiveStep(inferred);
        setMaxReachedStep(inferred);
        setStatus("active");

        // Silently refresh feasibility for resumed drafts so providerCount /
        // nearestVarikkoKm reflect current coverage instead of the synthesized
        // { deliverable: true } stub. Fire-and-forget — the stub stays in place
        // on failure, and we deliberately skip the "checking" UI to avoid a
        // loading flicker on a flow the user already considers ready.
        if (hydrated.osoite) {
            checkAddress({
                osoite: hydrated.osoite,
                lat: hydrated.lat,
                lng: hydrated.lng,
                placeId: hydrated.placeId,
                formattedAddress: hydrated.formattedAddress,
            }).then((result) => {
                if (result) setDraft((prev) => ({ ...prev, feasibility: result }));
            }).catch((err) => {
                captureError(err, {
                    tags: { entity: "pumppuRequest", operation: "checkAddress.silent-rehydrate" },
                });
            });
        }

        // Seed the attachments list for resumed drafts. Direct call (not via
        // refetchAttachments) because serverDraftId hasn't propagated to state
        // yet on this render; pass the just-claimed/resumed id explicitly.
        listPumppuRequestAttachments({
            pumppuRequestId: row.pumppuRequestId,
            token: tokenRef.current,
        }).then((list) => {
            if (Array.isArray(list)) setAttachments(list);
        }).catch((err) => {
            if (err?.status === 401) return; // detect() handler logs out
            captureError(err, { tags: { entity: "attachment", operation: "list.hydrate" } });
        });
    }, []);

    // Refetch attachments from the dedicated /pumppuRequest/urls endpoint —
    // gives us fresh blob SAS URLs (1h TTL). Soft-fail: empty list on error so
    // the UI is responsive even if the network is flaky.
    const refetchAttachments = useCallback(async (id) => {
        const targetId = id ?? serverDraftId;
        if (!targetId || !tokenRef.current) return;
        try {
            const list = await listPumppuRequestAttachments({
                pumppuRequestId: targetId,
                token: tokenRef.current,
            });
            setAttachments(Array.isArray(list) ? list : []);
        } catch (err) {
            if (err?.status === 401) { logout(); return; }
            captureError(err, { tags: { entity: "attachment", operation: "list" } });
        }
    }, [serverDraftId, logout]);

    // Shared "claim a local draft → server → hydrate" sequence used by the
    // detect auto-claim fallback and the conflict-resolution resumeLocal handler.
    // Optional isCancelled guard skips the post-await side effects (state writes,
    // localStorage writes) if the caller's scope has been torn down — relevant
    // for the detect effect, harmless for user-initiated callers that pass nothing.
    const claimLocalAndHydrate = useCallback(async (localData, isCancelled) => {
        const claimed = await createDraft({
            token: tokenRef.current,
            draft: localData,
            sessionId: sessionIdRef.current,
            language: lang,
        });
        if (isCancelled?.()) return null;
        writeActiveDraftId(claimed.pumppuRequestId);
        clearDraft();
        hydrateFromServer({
            ...localData,
            pumppuRequestId: claimed.pumppuRequestId,
            status: claimed.status,
            asiakasId: claimed.asiakasId,
        });
        return claimed;
    }, [hydrateFromServer, lang]);

    // Conflict auto-resolve (fb#170): when a logged-in user has BOTH a server
    // draft and a fresh local draft (the /laskuri "Käytä tarjouksessa" hand-off
    // that writeDraft's just maaraM3 + kesto + floorPlan, or a post-logout/login
    // local draft), MERGE the local fields into the existing server draft instead
    // of deleting it. Delete-and-claim used to destroy server-only data — address,
    // attachments, other spec fields — that a partial local draft didn't carry.
    // Local wins per field; betoniLaatuSelvitys is deep-merged so the laskuri
    // floorPlan is added without dropping the server's spec trail; the server
    // draft id (and its attachments, keyed by that id) is preserved.
    const mergeLocalIntoServerDraft = useCallback(async (serverRow, localData, isCancelled) => {
        const { merged, patchFields } = mergeLocalIntoServerRow(serverRow, localData, WIZARD_FIELDS);
        if (Object.keys(patchFields).length > 0) {
            try {
                await patchDraft({ token: tokenRef.current, id: serverRow.pumppuRequestId, fields: patchFields });
            } catch (err) {
                if (err?.status === 401) { logout(); return false; }
                captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.merge-local" } });
                return false;
            }
        }
        if (isCancelled?.()) return false;
        writeActiveDraftId(serverRow.pumppuRequestId);
        clearDraft();
        hydrateFromServer(merged);
        return true;
    }, [hydrateFromServer, logout]);

    // ------- Resume detection on mount + when auth flips -------
    useEffect(() => {
        let cancelled = false;
        async function detect() {
            setStatus("loading");
            try {
                if (isAuthenticated) {
                    const activeId = readActiveDraftId();
                    const localForAuth = readDraft();
                    const hasLocal = !!(localForAuth && (localForAuth.osoite || localForAuth.maaraM3));
                    if (activeId) {
                        const row = await getDraft({ token: tokenRef.current, id: activeId })
                            .catch((err) => {
                                if (err?.status === 401) { logout(); return null; }
                                // 404 here is expected: stale localStorage activeDraftId after the
                                // row was deleted/sent or belongs to a different person. The
                                // clearActiveDraftId() below recovers cleanly — not Sentry-worthy.
                                if (err?.status === 404) return null;
                                captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.get" } });
                                return null;
                            });
                        if (cancelled) return;
                        if (row) {
                            if (hasLocal) {
                                // Merge the local draft into the server draft (keeps
                                // its address/attachments — fb#170).
                                if (await mergeLocalIntoServerDraft(row, localForAuth, () => cancelled)) {
                                    return;
                                }
                                // Merge failed — fall back to hydrating the server row
                                // so the user isn't stuck on a loading screen.
                            }
                            hydrateFromServer(row);
                            return;
                        }
                        clearActiveDraftId();
                    }
                    // /draft/active returns the full draft shape (same columns
                    // as /:id/draft), so both the auto-resolve and auth-remote
                    // paths below can hydrate directly from this single fetch.
                    const remote = await getActiveDraft({ token: tokenRef.current })
                        .catch(handleAuthError("pumppuRequest", "draft.getActive"));
                    if (cancelled) return;
                    if (remote) {
                        if (hasLocal) {
                            if (await mergeLocalIntoServerDraft(remote, localForAuth, () => cancelled)) {
                                return;
                            }
                            // Fall through to auto-resume the server draft on merge failure.
                        }
                        if (cancelled) return;
                        // Auto-resume instead of blocking on a chooser screen: persist the
                        // pointer, hydrate the draft, and surface a dismissible inline notice.
                        // (hydrateFromServer sets status="active".) Replaces the old
                        // full-screen resume-prompt takeover that broke the flow right after login.
                        writeActiveDraftId(remote.pumppuRequestId);
                        hydrateFromServer(remote);
                        setResumeNotice({
                            at: remote.createdAt ?? null,
                            address: displayAddress(remote.formattedAddress || remote.osoite) || null,
                        });
                        return;
                    }
                    // Auth branch fallback: rescue a local draft from an authenticated
                    // mid-wizard session. persistAfterStepChange writes to localStorage
                    // whenever serverDraftId is null (claim only happens at Lähetä), so
                    // on reload the user has data in localStorage but no server row.
                    // Without this rescue, the draft is orphaned.
                    if (hasLocal) {
                        try {
                            await claimLocalAndHydrate(localForAuth, () => cancelled);
                            return;
                        } catch (err) {
                            captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.auto-claim" } });
                            // Fall through to setStatus("active"); local stays in storage
                            // so a future retry can still pick it up.
                        }
                    }
                } else {
                    const local = readDraft();
                    if (local && (local.osoite || local.maaraM3)) {
                        // Anon resume always re-prompts for Gate-1 acceptance — simpler than
                        // comparing stored TERMS_VERSION, acceptable because anon drafts are
                        // short-lived and the request is non-binding.
                        const hydrated = { ...INITIAL_DRAFT, ...local, termsAccepted: false };
                        setDraft(hydrated);
                        // Land on the inferred step and surface the inline resume notice
                        // instead of the old full-screen prompt.
                        const inferred = inferActiveStep(hydrated);
                        setActiveStep(inferred);
                        setMaxReachedStep(inferred);
                        setResumeNotice({
                            at: local._savedAt ?? null,
                            address: displayAddress(local.formattedAddress || local.osoite) || null,
                        });
                        setStatus("active");
                        return;
                    }
                }
                setStatus("active");
            } catch (err) {
                captureError(err, { tags: { entity: "pumppuRequest", operation: "resume-detect" } });
                setStatus("active");
            }
        }
        detect();
        return () => { cancelled = true; };
        // tokenRef.current is read at call time; token transitions are covered
        // by isAuthenticated flips (login/logout). A silent token refresh
        // should not reset the wizard, so token is intentionally not a dep.
        // logout is a stable useCallback from AuthContext — included only to
        // satisfy exhaustive-deps; it won't trigger re-runs.
    }, [isAuthenticated, hydrateFromServer, claimLocalAndHydrate, mergeLocalIntoServerDraft, handleAuthError, logout]);

    // ------- Fetch asiakases when authenticated (for multi-asiakas branch on step 5) -------
    // Deps: ownerAsiakasId is the single field defaultAsiakasFromList reads from
    // the person; personId refetches the list whenever the active identity changes.
    // personId matters under impersonation — every betonijerry user shares the
    // umbrella ownerAsiakasId (1349), so without it the list (and the
    // AsiakasEditDialog rows derived from it) would stay the admin's and the edit
    // modal would fetch a foreign asiakasId with the impersonation token → 403.
    const ownerAsiakasId = person?.ownerAsiakasId ?? null;
    const personId = person?.personId ?? null;
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        let cancelled = false;
        (async () => {
            const list = await listMeAsiakases({ token: tokenRef.current })
                .catch(handleAuthError("asiakas", "list"));
            if (cancelled || !list) return;
            setFetchedAsiakases(list);
            // Only auto-pick on first load — don't overwrite a user's explicit choice on refetch.
            setSelectedAsiakasId((prev) => prev || defaultAsiakasFromList(list, { ownerAsiakasId }) || "");
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated, handleAuthError, ownerAsiakasId, personId]);

    // Debounced step-2 boom capability check. Coords come from the step-1
    // feasibility pass; only a finite positive puomi triggers. Failures are
    // silent (captureError + hide the row) — this is informational UI and must
    // never block the wizard or surface the checkAddress rate limiter.
    useEffect(() => {
        // Wrapped in inner async fn so react-hooks/set-state-in-effect does not
        // flag the synchronous setBoomCheck reset inside the guard block.
        let timer;
        async function run() {
            if (activeStep !== PUMP_STEP_INDEX) {
                setBoomCheck({ status: "idle", providerCount: null, forPuomi: null });
                return;
            }
            const puomiNum = Number(draft.puomi);
            const hasCoords =
                Number.isFinite(draft.lat) && Number.isFinite(draft.lng) &&
                typeof draft.placeId === "string" && draft.placeId.length > 0;
            if (!hasCoords || draft.puomi === "" || !Number.isFinite(puomiNum) || puomiNum <= 0) {
                setBoomCheck({ status: "idle", providerCount: null, forPuomi: null });
                return;
            }
            const seq = ++boomCheckSeqRef.current;
            setBoomCheck((prev) =>
                prev.status === "done" && prev.forPuomi === puomiNum
                    ? prev            // already showing the right result — don't flicker
                    : { ...prev, status: "checking" }
            );
            timer = setTimeout(async () => {
                try {
                    const result = await checkAddress({
                        osoite: draft.osoite,
                        lat: draft.lat,
                        lng: draft.lng,
                        placeId: draft.placeId,
                        formattedAddress: draft.formattedAddress,
                        requiredPuomi: puomiNum,
                        token: tokenRef.current,
                    });
                    if (seq !== boomCheckSeqRef.current) return;
                    setBoomCheck({ status: "done", providerCount: result?.providerCount ?? null, forPuomi: puomiNum });
                } catch (err) {
                    if (seq !== boomCheckSeqRef.current) return;
                    // 429 = the checkAddress limiter doing its job on rapid preset
                    // clicks — expected, not Sentry-worthy.
                    if (err?.status !== 429) {
                        captureError(err, { tags: { entity: "pumppuRequest", operation: "checkAddress.boom" } });
                    }
                    setBoomCheck({ status: "idle", providerCount: null, forPuomi: null });
                }
            }, 500);
        }
        run();
        return () => clearTimeout(timer);
    }, [activeStep, draft.puomi, draft.lat, draft.lng, draft.placeId, draft.osoite, draft.formattedAddress]);

    // Picker change → state update + server PATCH so the choice survives reload.
    // "private" sentinel needs an extra round-trip to find-or-create the user's
    // typeId=2 row; we resolve to a numeric id BEFORE setting picker state so
    // the UI never lingers on the sentinel (matters on the create-private error
    // path — a transient "private" pick that never reconciles is confusing).
    // Orphan private asiakases from abandoned wizards are pruned by cleanup.
    //
    // Rapid picks: each call aborts the previous in-flight request so the
    // server can't resolve writes out of order.
    const handleSelectAsiakas = useCallback(async (value) => {
        if (!value) { setSelectedAsiakasId(""); return; }
        if (!isAuthenticated || !serverDraftId) { setSelectedAsiakasId(value); return; }

        if (selectAsiakasAbortRef.current) selectAsiakasAbortRef.current.abort();
        const controller = new AbortController();
        selectAsiakasAbortRef.current = controller;
        const { signal } = controller;

        let asiakasId;
        if (value === LASKUTUS_PRIVATE_SENTINEL) {
            try {
                const resolved = await createMePrivateAsiakas({ token: tokenRef.current, signal });
                if (signal.aborted) return;
                asiakasId = resolved.asiakasId;
                setFetchedAsiakases((prev) =>
                    prev.some((a) => a.asiakasId === asiakasId) ? prev : [...prev, resolved]
                );
            } catch (err) {
                if (signal.aborted || err?.name === "AbortError") return;
                captureError(err, { tags: { entity: "asiakas", operation: "create-private" } });
                setSubmitError(resolveErrorMessage(err, { t, lang, fallbackKey: "wizard.privateBillingCreateFailed" }));
                return;
            }
        } else {
            asiakasId = Number(value);
            if (!Number.isFinite(asiakasId)) return;
        }

        setSelectedAsiakasId(String(asiakasId));
        patchDraft({ token: tokenRef.current, id: serverDraftId, fields: { asiakasId }, signal })
            .catch((err) => {
                if (signal.aborted || err?.name === "AbortError") return;
                captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.patch.asiakasId" } });
            });
    }, [isAuthenticated, serverDraftId, lang, t]);

    // Yhteystiedot Muokkaa → person dialog (always).
    const handleEditPerson = useCallback(() => {
        setEditDialog({ kind: "person", asiakas: null });
    }, []);

    // Laskutus Muokkaa → routes by the selected asiakas's typeId.
    // typeId=2 (private) edits via the person dialog so the asiakas name
    // stays in sync with etu/sukunimi. typeId=1 (company) edits in place.
    const handleEditSelectedAsiakas = useCallback((id) => {
        const row = fetchedAsiakases.find((a) => a.asiakasId === id);
        if (!row) return;
        if (row.asiakasTypeId === 2) {
            setEditDialog({ kind: "person", asiakas: null });
        } else {
            setEditDialog({ kind: "asiakas", asiakas: row });
        }
    }, [fetchedAsiakases]);

    const closeEditDialog = useCallback(() => {
        setEditDialog({ kind: null, asiakas: null });
    }, []);

    // After either dialog saves, refetch the asiakas list so the dropdown
    // reflects new names / addresses (and so PersonEditDialog's address-only
    // save shows up as a new typeId=2 row when one was just created).
    const refetchAsiakases = useCallback(async () => {
        const list = await listMeAsiakases({ token: tokenRef.current })
            .catch(handleAuthError("asiakas", "refetch-after-edit"));
        if (list) setFetchedAsiakases(list);
    }, [handleAuthError]);

    const handleCreateCompany = useCallback(async ({ ytunnus, asiakasNimi }) => {
        const created = await createMeAsiakas({
            token: tokenRef.current,
            ytunnus,
            asiakasNimi,
        });

        // Refetch the asiakas list so the picker shows the new (or pre-existing) row.
        // Soft-fail — the row exists server-side; user can refresh later.
        const list = await listMeAsiakases({ token: tokenRef.current })
            .catch(handleAuthError("asiakas", "refetch-after-create"));
        if (list) setFetchedAsiakases(list);

        setSelectedAsiakasId(String(created.asiakasId));
        return created;
    }, [handleAuthError]);

    // ------- Persistence machine -------
    // localStorage is always the safety net — survives PATCH failures and refresh
    // mid-wizard regardless of auth state. The backend draft (only present after
    // claim() — at Lähetä for fresh-start users, or at /draft/active resume for
    // returning users) is the authoritative store at send time.
    // PATCH-per-step therefore only fires in the post-resume minority path; for
    // fresh-start authenticated users localStorage carries the wizard until
    // claim() at final submit.
    const persistAfterStepChange = useCallback((prevStep, nextDraft) => {
        writeDraft(nextDraft);

        if (!serverDraftId) {
            // Pre-claim (anonymous or fresh-auth): record per-step telemetry.
            postAnonymousEvent({
                sessionId: sessionIdRef.current,
                step: prevStep + 1,
                partialDraft: pick(nextDraft, STEP_FIELDS[prevStep]),
            });
            return;
        }
        // Post-claim (resumed server draft): mirror the step's fields to the
        // backend. Failure is tolerable since localStorage already has the snapshot.
        const fields = pick(nextDraft, STEP_FIELDS[prevStep]);
        if (Object.keys(fields).length === 0) return;
        patchDraft({ token: tokenRef.current, id: serverDraftId, fields })
            .catch((err) => {
                captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.patch" } });
            });
    }, [serverDraftId]);

    const onChange = useCallback((patch) => {
        setDraft((prev) => {
            const next = { ...prev, ...patch };
            // Pre-claim: live localStorage write so refresh mid-typing doesn't lose work.
            if (!isAuthenticated || !serverDraftId) writeDraft(next);
            return next;
        });
        // Clear errors for any patched keys via functional update — keeps the
        // onChange identity stable across renders.
        setErrors((prev) => {
            const keys = Object.keys(patch);
            if (!keys.some((k) => prev[k])) return prev;
            const next = { ...prev };
            for (const k of keys) delete next[k];
            return next;
        });
        if ("osoite" in patch) {
            setAddressCheckStatus("idle");
            setAddressCheckError(null);
        }
    }, [isAuthenticated, serverDraftId]);

    const canAdvance = useMemo(() => {
        const stepValidator = validatorForStep(activeStep);
        const stepErrors = activeStep === TOTAL_STEPS - 1
            ? stepValidator(draft, { isAuthenticated, selectedAsiakasId })
            : stepValidator(draft);
        if (Object.keys(stepErrors).length > 0) return false;
        // Block "Lähetä tarjouspyyntö" while the Laskutus picker is mid-edit
        // (dropdown or create-new panel visible) — the user must commit the
        // selection before sending.
        if (activeStep === TOTAL_STEPS - 1 && laskutusEditing) return false;
        // Block Seuraava while attachments are still uploading — leaving the
        // Lisätiedot step unmounts AttachmentsSection and abandons the upload.
        if (activeStep === DETAILS_STEP_INDEX && attachmentsUploading) return false;
        return true;
    }, [activeStep, draft, isAuthenticated, selectedAsiakasId, laskutusEditing, attachmentsUploading]);

    // Human-readable reason the forward/submit button is blocked, surfaced next
    // to the disabled button so the user never faces a dead control with no
    // explanation. Mirrors canAdvance's gates; null when nothing blocks.
    const nextBlockedReason = useMemo(() => {
        // Step 1 (osoite) speaks for itself — the required field + the "Tarkista
        // osoite" button label guide the user. A pre-emptive "Anna työmaan osoite"
        // hint before any interaction just reads as a scold (geocode failures still
        // surface on the field + feasibility panel).
        if (activeStep === 0) return null;
        if (activeStep === DETAILS_STEP_INDEX && attachmentsUploading) {
            return t("landing.requestForm.waitingForAttachments");
        }
        if (activeStep === TOTAL_STEPS - 1 && laskutusEditing) {
            return t("landing.requestForm.confirmBillingToContinue");
        }
        const stepValidator = validatorForStep(activeStep);
        const stepErrors = activeStep === TOTAL_STEPS - 1
            ? stepValidator(draft, { isAuthenticated, selectedAsiakasId })
            : stepValidator(draft);
        // quoteValidation's error maps hold i18n key paths, not display text —
        // resolve through t() before returning (see quoteValidation.js:4).
        const errorKey = Object.values(stepErrors)[0];
        return errorKey ? t(errorKey) : null;
    }, [activeStep, draft, isAuthenticated, selectedAsiakasId, laskutusEditing, attachmentsUploading, t]);

    const onBack = useCallback(() => {
        setActiveStep((s) => (s === 0 ? 0 : s - 1));
    }, []);

    async function runAddressCheck(overrides) {
        const osoite = (overrides?.osoite ?? draft.osoite ?? "").trim();
        if (!osoite) return null;
        setAddressCheckStatus("checking");
        setAddressCheckError(null);
        try {
            const result = await checkAddress({
                osoite,
                lat: overrides?.lat,
                lng: overrides?.lng,
                placeId: overrides?.placeId,
                formattedAddress: overrides?.formattedAddress,
                token: tokenRef.current,
            });
            // Developer-only: backend returns `providers` (covering pumppufirmat,
            // nearest first) when the caller's token is a developer/admin. Log each
            // so devs can see exactly who would receive this request's fanout.
            if (Array.isArray(result?.providers)) {
                console.log(`[checkAddress] ${result.providers.length} pumppufirma(a) kattaa osoitteen "${osoite}":`);
                result.providers.forEach((p, i) => {
                    console.log(`  ${i + 1}. ${p.asiakasNimi} (asiakasId ${p.asiakasId}) — ${p.distanceKm?.toFixed?.(1) ?? p.distanceKm} km`);
                });
            }
            onChange({
                feasibility: result,
                lat: result.lat ?? overrides?.lat ?? null,
                lng: result.lng ?? overrides?.lng ?? null,
                placeId: result.placeId ?? overrides?.placeId ?? null,
                formattedAddress: result.formattedAddress ?? overrides?.formattedAddress ?? null,
            });
            setAddressCheckStatus("idle");
            return result;
        } catch (err) {
            captureError(err, {
                tags: { entity: "pumppuRequest", operation: "checkAddress" },
                extra: { osoite },
            });
            // Clear stale feasibility — without this, a prior successful result
            // remains in state and the step-0 validator would still pass even
            // though the address could not be verified this time around.
            setDraft((prev) => ({ ...prev, feasibility: null }));
            setAddressCheckError(err);
            setAddressCheckStatus("error");
            return null;
        }
    }

    async function handleAddressPicked({ osoite, lat, lng, placeId, formattedAddress }) {
        onChange({
            osoite,
            formattedAddress,
            placeId,
            lat,
            lng,
            feasibility: null,
        });
        await runAddressCheck({ osoite, lat, lng, placeId, formattedAddress });
    }

    async function advance(draftForPersist) {
        const nextStep = Math.min(TOTAL_STEPS - 1, activeStep + 1);
        persistAfterStepChange(activeStep, draftForPersist);
        // Pre-claim the server draft when authenticated users land on the
        // Lisätiedot step (step 4, index 3) where attachments live. The
        // AttachmentsSection needs a stable pumppuRequestId for uploads, and
        // pre-claiming on transition (rather than at first Lisää click) keeps
        // the click latency-free. Soft-fail: leave the user on the step even if
        // claim throws — AttachmentsSection.onPreClaim re-attempts on the first
        // Lisää click.
        if (isAuthenticated && !serverDraftId && nextStep === DETAILS_STEP_INDEX) {
            try {
                const id = await claim(tokenRef.current);
                // Best-effort attachments seed — usually no-op on a fresh claim,
                // but covers the edge where the user already had server-side
                // attachments via another tab on the same draft.
                refetchAttachments(id);
            } catch (err) {
                captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.preclaim" } });
            }
        }
        setActiveStep(nextStep);
        setMaxReachedStep((m) => Math.max(m, nextStep));
    }

    async function onNext() {
        // Step 0: SEURAAVA doubles as TARKISTA OSOITE on first click.
        // First click runs the feasibility check; auto-advances only if deliverable.
        // Geocode-fail keeps user here (error surfaces via validator). No-supply
        // keeps user here so they see the result panel; second click then advances
        // through the normal path below since validateAddress no longer requires
        // feasibility (deliverable=false is an acceptable no_supply request).
        if (activeStep === 0 && !draft.feasibility) {
            if (addressCheckStatus === "checking") return;
            const result = await runAddressCheck();
            if (!result || result.geocoded === false || result.deliverable === false) return;
            await advance({
                ...draft,
                feasibility: result,
                lat: result.lat ?? null,
                lng: result.lng ?? null,
                placeId: result.placeId ?? null,
                formattedAddress: result.formattedAddress ?? null,
            });
            return;
        }
        const stepValidator = validatorForStep(activeStep);
        const errs = activeStep === TOTAL_STEPS - 1
            ? stepValidator(draft, { isAuthenticated, selectedAsiakasId })
            : stepValidator(draft);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        await advance(draft);
    }

    const onJumpTo = useCallback((idx) => {
        setActiveStep((cur) => (idx > maxReachedStep ? cur : idx));
    }, [maxReachedStep]);

    // ------- Claim routine: localStorage -> SQL draft on auth -------
    async function claim(authedToken) {
        if (serverDraftId) return serverDraftId;
        try {
            const r = await createDraft({
                token: authedToken,
                draft,
                sessionId: sessionIdRef.current,
                language: lang,
            });
            setServerDraftId(r.pumppuRequestId);
            writeActiveDraftId(r.pumppuRequestId);
            clearDraft();
            return r.pumppuRequestId;
        } catch (err) {
            captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.create" } });
            throw err;
        }
    }

    // ------- Final submit -------
    async function onSubmit() {
        const reviewErrors = validateReview(draft, { isAuthenticated, selectedAsiakasId });
        if (Object.keys(reviewErrors).length > 0) {
            setErrors(reviewErrors);
            return;
        }

        const errs = validateAll(draft);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            // Jump to first failing step
            for (let s = 0; s < TOTAL_STEPS; s++) {
                if (Object.keys(validatorForStep(s)(draft)).length > 0) {
                    setActiveStep(s);
                    return;
                }
            }
            return; // safety net — don't fall through to claim/send
        }

        if (!isAuthenticated) {
            openAuthModal({
                onSuccess: async () => {
                    try {
                        const id = await claim(tokenRef.current);
                        await finalSend(id);
                    } catch (err) {
                        setSubmitError(resolveErrorMessage(err, { t, lang, fallbackKey: "wizard.sendFailed" }));
                    }
                },
            });
            return;
        }
        try {
            const id = await claim(tokenRef.current);
            await finalSend(id);
        } catch (err) {
            setSubmitError(resolveErrorMessage(err, { t, lang, fallbackKey: "wizard.sendFailed" }));
        }
    }

    async function finalSend(idOverride) {
        setStatus("submitting");
        setSubmitError(null);
        try {
            const id = idOverride ?? serverDraftId; // prefer the passed-in id
            // Pohjakuva snapshot: best-effort. Uploaded while the request is
            // still a draft; failure or slowness is reported but NEVER blocks
            // the send for longer than the timeout.
            const floorPlan = draft.betoniLaatuSelvitys?.floorPlan;
            if (floorPlan?.shapes?.length && personRef.current?.personId && snapshotUploadedForRef.current !== id) {
                try {
                    await Promise.race([
                        (async () => {
                            const svg = planToSvgString(floorPlan, {
                                widthPx: 1200,
                                caption: floorPlanSummary(floorPlan, lang, t("laskuri.floorPlanSummary.recommendedWord")) ?? "",
                                lang,
                                pillarWord: t("laskuri.shapePanel.typeLabels.pillar"),
                            });
                            const png = await svgToPngBlob(svg, 1200);
                            const file = new File([png], "pohjakuva.png", { type: "image/png" });
                            await uploadPumppuRequestAttachment({
                                file,
                                pumppuRequestId: id,
                                token: tokenRef.current,
                                ownerAsiakasId: personRef.current?.ownerAsiakasId,
                                entryByPersonId: personRef.current?.personId,
                                fileComment: t("landing.requestForm.floorPlanSnapshotComment"),
                            });
                            snapshotUploadedForRef.current = id;
                        })(),
                        new Promise((unused, reject) => {
                            setTimeout(() => reject(new Error("laskuri snapshot timed out after 10000 ms")), 10000);
                        }),
                    ]);
                } catch (err) {
                    captureError(err, { tags: { entity: "pumppuRequest", operation: "laskuri.snapshot", swallowed: true } });
                }
            }
            const sendResult = await sendDraft({
                token: tokenRef.current,
                id,
                asiakasId: resolveAsiakasIdForSend(selectedAsiakasId, fetchedAsiakases),
                // Gate-1: stamp the fetched consent version on this specific pumppuRequest row.
                // Backend writes pumppuRequest.termsVersion + termsAcceptedAt alongside
                // the status='draft' → 'open'/'no_supply' flip.
                termsVersion: consentVersion,
                language: lang,
            });
            clearDraft();
            clearActiveDraftId();
            setResult({ pumppuRequestId: id, status: sendResult.status });
            setStatus("done");
            // Campaign conversion: a sent tarjouspyyntö is the primary lead event.
            // No-op unless GA4/Ads is configured; fires for both open and no_supply.
            trackConversion({ currency: "EUR", value: 0, request_status: sendResult.status });
        } catch (err) {
            captureError(err, { tags: { entity: "pumppuRequest", operation: "send" } });
            setSubmitError(resolveErrorMessage(err, { t, lang, fallbackKey: "wizard.sendFailed" }));
            setStatus("active");
        }
    }

    // ------- Resume notice handlers -------
    // Dismiss only hides the notice — the resumed draft stays loaded.
    const dismissResumeNotice = useCallback(() => setResumeNotice(null), []);

    // "Aloita alusta" — discard the resumed draft and reset to a blank step 1.
    // Destructive: deletes the server draft when one exists (gated behind the
    // QuoteResumeNotice inline confirm). serverDraftId covers both the auto-resumed
    // auth-remote draft and the activeId path; anon drafts have no server row.
    async function startFresh() {
        if (serverDraftId) {
            await deleteDraft({ token: tokenRef.current, id: serverDraftId })
                .catch(handleAuthError("pumppuRequest", "draft.delete"));
        }
        clearDraft();
        clearActiveDraftId();
        setDraft(INITIAL_DRAFT);
        setActiveStep(0);
        setMaxReachedStep(0);
        setServerDraftId(null);
        setResumeNotice(null);
        setStatus("active");
    }

    // ------- Render -------
    let content;
    if (status === "loading") {
        content = (
            <Stack spacing={2} sx={{ alignItems: "center", py: 6 }}>
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("common.loading")}</Typography>
            </Stack>
        );
    } else if (status === "done") {
        content = (
            <KiitosScreen result={result} email={person?.email} />
        );
    } else {
        content = (
            <>
                <QuoteWizard
                    activeStep={activeStep}
                    maxReachedStep={maxReachedStep}
                    draft={draft}
                    errors={errors}
                    resumeNotice={resumeNotice}
                    onStartOver={startFresh}
                    onDismissResume={dismissResumeNotice}
                    submitting={status === "submitting"}
                    asiakases={asiakases}
                    selectedAsiakasId={selectedAsiakasId}
                    canAdvance={canAdvance}
                    nextBlockedReason={nextBlockedReason}
                    nextPending={addressCheckStatus === "checking"}
                    addressCheckStatus={addressCheckStatus}
                    addressCheckError={addressCheckError}
                    boomCheck={boomCheck}
                    onAddressRetry={runAddressCheck}
                    onAddressPicked={handleAddressPicked}
                    onChange={onChange}
                    onJumpTo={onJumpTo}
                    onBack={onBack}
                    onNext={onNext}
                    onSubmit={onSubmit}
                    onSelectAsiakas={handleSelectAsiakas}
                    onCreateCompany={handleCreateCompany}
                    onEditPerson={handleEditPerson}
                    onEditSelectedAsiakas={handleEditSelectedAsiakas}
                    onLaskutusEditingChange={setLaskutusEditing}
                    onClose={() => navigate("/")}
                    isAuthenticated={isAuthenticated}
                    person={person}
                    onOpenLogin={() => openAuthModal()}
                    onOpenRegister={() => openAuthModal()}
                    attachments={attachments}
                    consentCopy={consentCopy}
                    pumppuRequestId={serverDraftId}
                    onAttachmentsUploadingChange={setAttachmentsUploading}
                    onRefetchAttachments={() => refetchAttachments()}
                    onPreClaimDraft={async () => {
                        if (serverDraftId) return serverDraftId;
                        try {
                            const id = await claim(tokenRef.current);
                            return id;
                        } catch (err) {
                            captureError(err, { tags: { entity: "pumppuRequest", operation: "draft.preclaim.lazy" } });
                            return null;
                        }
                    }}
                />
                {submitError && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>{submitError}</Typography>
                )}
            </>
        );
    }
    return (
        <TilaaBackdrop>
            {content}
            {editDialog.kind === "person" && (
                <PersonEditDialog
                    open
                    onClose={closeEditDialog}
                    person={person}
                    privateAsiakas={privateAsiakas}
                    siteAddress={draft.formattedAddress || null}
                    onSaved={refetchAsiakases}
                />
            )}
            {editDialog.kind === "asiakas" && (
                <AsiakasEditDialog
                    open
                    onClose={closeEditDialog}
                    asiakas={editDialog.asiakas}
                    onSaved={refetchAsiakases}
                />
            )}
        </TilaaBackdrop>
    );
}
